import { Text, UnstyledButton } from '@mantine/core';
import { Link } from 'react-router-dom';
import type { ConversationRecap } from '@nelnela/contracts';
import { recapHeadline } from '../../lib/chat';

/**
 * The end-of-conversation recap (F13: "recap — turns, words used well, 2-3 corrections worth
 * remembering").
 *
 * **There is no design frame for this one**, and M6C-HANDOFF says which register to build it in:
 * `7a`'s — "a report, not a game dashboard". So: numbers stated once, no streak, no score, no
 * congratulation. The two lists are the whole of it.
 *
 * **It is derived, not generated** — `lib/recap.ts` ranks what is already stored and no second
 * model call is made at the end of a conversation that has already made one per turn. That is why
 * every line here is something the learner has already seen: the corrections re-show the model's
 * own explanation rather than commissioning new writing about them.
 *
 * **It says nothing about tomorrow**, deliberately. `conversationEnd` kicks the fold, but M6a's
 * standing gap 3 means the fold can stop short of these events on the first pass, so a recap that
 * promised "these come back tomorrow" would sometimes be lying. It reports the conversation that
 * just happened, which it can always be right about.
 */

export interface RecapPanelProps {
  recap: ConversationRecap;
  /** the scenario, in the header — `scenarioLabel(conversation.scenario)` */
  title: string;
  onDone: () => void;
}

export function RecapPanel({ recap, title, onDone }: RecapPanelProps) {
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 18px 24px' }}>
      <div style={{ fontSize: 15, color: 'var(--nn-text-strong)' }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--nn-muted)', marginTop: 3 }}>
        {recapHeadline(recap)}
      </div>

      <Section label="Used well">
        {recap.usedWell.length === 0 ? (
          <Text fz={11.5} c="var(--nn-faint)">
            {/* The honest version. `usedWell` is empty when nothing was produced cleanly,
                unprompted AND already met — which is the normal case early on, and saying so is
                better than an empty box the learner has to interpret. */}
            Nothing to report here yet — this counts words you had already met and used without a
            chip.
          </Text>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {recap.usedWell.map((w) => (
              <span key={w.itemId} style={wordChip}>
                <span lang="ka" style={{ color: 'var(--nn-text-strong)' }}>
                  {w.display}
                </span>
                {w.gloss && <span style={{ color: 'var(--nn-faint)' }}> · {w.gloss}</span>}
              </span>
            ))}
          </div>
        )}
      </Section>

      <Section label="Worth remembering">
        {recap.worthRemembering.length === 0 ? (
          <Text fz={11.5} c="var(--nn-faint)">
            Nothing came up worth writing down.
          </Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {recap.worthRemembering.map((c) => (
              <div key={c.id} style={correctionRow}>
                <div lang="ka" style={{ fontSize: 13.5, color: 'var(--nn-text)' }}>
                  {c.original} → <b style={{ color: 'var(--nn-text-strong)' }}>{c.suggested}</b>
                </div>
                {/* The model's own line if it wrote one, else the vocabulary's label for the tag.
                    Never nothing: a mistake named with no reason is an accusation. */}
                <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--nn-muted)' }}>
                  {c.explanation || c.errorLabel}
                </div>
                {c.lessonId && (
                  <UnstyledButton
                    component={Link}
                    to={`/words/lesson/${c.lessonId}`}
                    style={{
                      fontSize: 11,
                      color: 'var(--nn-link)',
                      marginTop: 5,
                      display: 'block',
                    }}
                  >
                    lesson →
                  </UnstyledButton>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <UnstyledButton onClick={onDone} style={{ ...doneButton, marginTop: 20 }}>
        Done
      </UnstyledButton>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          fontSize: 10.5,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: 'var(--nn-faint)',
          marginBottom: 7,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const wordChip: React.CSSProperties = {
  fontSize: 12,
  padding: '4px 10px',
  borderRadius: 999,
  border: '1px solid var(--nn-hairline)',
  background: 'var(--nn-panel)',
};

const correctionRow: React.CSSProperties = {
  background: 'var(--nn-panel)',
  border: '1px solid var(--nn-hairline)',
  borderRadius: 10,
  padding: '10px 13px',
};

const doneButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '9px 16px',
  borderRadius: 7,
  background: 'var(--nn-accent)',
  color: 'var(--nn-on-accent)',
  fontSize: 12.5,
  fontWeight: 600,
};
